import React, { useMemo, useCallback } from "react";
import { ChartHeader } from "@/components/ChartHeader";
import { Card, CardContent } from "@/components/ui/Card";
import { useTranslation } from "react-i18next";
import { useOrg } from "@/hooks/useOrg";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { STUDENT_LIST_TIME_SERIES } from "@/api/queries/studentTimeSeries";
import { useQuery } from "@apollo/client";
import { StudentListTimeSeriesArgumentInput } from "@/__generated__/types";
import { getDateString } from "@/util/dateUtils";
import { isInDarkMode } from "@/util/themingUtils";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/Chart";
import { Skeleton } from "@/components/ui";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Area,
} from "recharts";
import {
  differenceInMonths,
  startOfMonth,
  parse,
  format,
  subYears,
} from "date-fns";
import { CircleX, Share, FileDown } from "lucide-react";
import {
  buildDateRangeMap,
  combineData,
  ChartDataPoint,
} from "@/util/chartUtils";
import { EmptyStateComponent } from "@/components/EmptyStateComponent";
import {
  aggregateData,
  RawDataPoint,
} from "@/components/ChartCard/utils/aggregateData";
import { useShareLink } from "@/hooks/useShareLink";
import { toast } from "sonner";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";
import { formatUSDateString } from "@/util/stringUtils";

type TimeSeriesType = keyof StudentListTimeSeriesArgumentInput;
export type DataInterval = "daily" | "monthly" | "weekly";

const CHART_HEIGHT = "328px";

interface AggregatedDataPoint {
  date: string;
  value: number;
  isPartial?: boolean;
  periodStart: Date;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { value: number; color: string }[];
  label?: string;
}) => {
  if (!active || !payload || !label) {
    return null;
  }

  const date = new Date(label + "T00:00:00");
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const value = payload[0]?.value;
  const formattedValue =
    typeof value === "number" ? value.toLocaleString() : value;
  const color = payload[0]?.color || "var(--color-chart-1)";

  return (
    <div className="rounded-lg border bg-popover p-3 text-popover-foreground shadow-lg">
      <div className="mb-2 font-bold">{formattedDate}</div>
      <div className="flex items-center gap-2">
        <div className="h-0.5 w-3 rounded" style={{ backgroundColor: color }} />
        <span>{formattedValue}</span>
      </div>
    </div>
  );
};

export const EngagementChart = ({
  title,
  seriesType,
  interval,
}: {
  title: string;
  seriesType: TimeSeriesType;
  interval: DataInterval;
}) => {
  const { org, loading: loadingOrg } = useOrg();
  const { cohortYear, loading: loadingCohortYear } = useCurrentCohortYear();
  const { t } = useTranslation();
  const { shareCurrentLink } = useShareLink();

  const endDate = useMemo(() => new Date(), []);

  const startDate = useMemo(() => {
    const today = new Date();
    return startOfMonth(subYears(today, 1));
  }, []);

  const argument = getArgument(startDate, endDate, cohortYear || 0, seriesType);

  const { data, loading, error } = useQuery(STUDENT_LIST_TIME_SERIES, {
    variables: {
      orgId: org?.id || "",
      argument,
    },
    skip: !org?.id || !cohortYear,
  });

  let chartData: ChartDataPoint[] = [];
  if (data?.node?.__typename === "Organization") {
    chartData =
      data?.node?.studentListTimeSeries?.map((dataPoint) => {
        return {
          date: dataPoint.date,
          current: dataPoint.value,
        };
      }) || [];
  }

  const combined = combineData(
    buildDateRangeMap(getDateString(startDate), getDateString(endDate)),
    [chartData],
  ) as { date: string; current: number }[];

  const config = {
    current: {
      color: "var(--color-chart-1)",
    },
  };

  const { chartData: aggregatedData } = chartProps(
    combined,
    startDate,
    endDate,
    interval,
  );

  const handleExportCSV = useCallback(() => {
    if (!aggregatedData || aggregatedData.length === 0) {
      toast.error("No data available to export");
      return;
    }

    try {
      const filenamePart = title
        .toLowerCase()
        .replace(/&\s*/g, "")
        .replace(/\s+/g, "-");

      const headers = ["", title];

      const exportData = aggregatedData.map((dataPoint) => ({
        "": formatUSDateString(dataPoint.date),
        [title]: dataPoint.current ?? "",
      }));

      exportChartCSV({
        filename: timestampFilename(filenamePart),
        headers,
        data: exportData,
      });

      toast.success(t("global.exportToCSV.success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  }, [aggregatedData, title, t]);

  const menuOptions = [
    {
      label: t("global.exportToCSV.menuTitle"),
      onClick: handleExportCSV,
      icon: <FileDown aria-hidden height={16} />,
    },
    {
      label: t("global.share"),
      onClick: shareCurrentLink,
      icon: <Share aria-hidden height={16} />,
    },
  ];

  return (
    <Card>
      <ChartHeader title={title} menuOptions={menuOptions}></ChartHeader>
      <Chart
        chartConfig={config}
        loading={loadingOrg || loadingCohortYear || loading}
        error={!!error}
        {...chartProps(combined, startDate, endDate, interval)}
      />
    </Card>
  );
};

function Chart({
  chartData,
  chartConfig,
  getTicks,
  tickFormatter,
  loading,
  error,
}: {
  chartData: { date: string; current: number }[];
  chartConfig: ChartConfig;
  getTicks: () => string[];
  tickFormatter?: (value: string | number | Date) => string;
  loading: boolean;
  error: boolean;
}) {
  const { t } = useTranslation();

  const gradientId = `chartGradient-${Math.random().toString(36).slice(2, 11)}`;

  if (error) {
    return (
      <CardContent className="grow">
        <EmptyStateComponent
          height={CHART_HEIGHT}
          message={t("engagement.chartError")}
          icon={<CircleX className="size-12" />}
        />
      </CardContent>
    );
  }

  return (
    <CardContent className="grow">
      {loading ? (
        <Skeleton className={`h-[${CHART_HEIGHT}] w-full`} />
      ) : (
        <ChartContainer
          className={`h-[${CHART_HEIGHT}] w-full`}
          config={chartConfig}
        >
          <ComposedChart accessibilityLayer data={chartData}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--chart-1-gradient)"
                  stopOpacity={isInDarkMode() ? 0.15 : 0.5}
                />
                <stop
                  offset="95%"
                  stopColor="var(--chart-1-gradient)"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              ticks={getTicks()}
              tickLine={false}
              axisLine={false}
              padding={{ left: 15, right: 15 }}
              tickMargin={7}
              tickFormatter={tickFormatter}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={50}
              tickFormatter={(value) => {
                return typeof value === "number"
                  ? value.toLocaleString()
                  : value;
              }}
            />
            <ChartTooltip cursor={false} content={<CustomTooltip />} />
            <Area
              dataKey="current"
              stroke="none"
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              type="linear"
              connectNulls={false}
            />
            {Object.entries(chartConfig).map(([dataKey, chart]) => {
              return (
                <Line
                  key={dataKey}
                  dataKey={dataKey}
                  type="linear"
                  stroke={chart.color}
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray={chart.dashed ? "5 5" : undefined}
                />
              );
            })}
          </ComposedChart>
        </ChartContainer>
      )}
    </CardContent>
  );
}

const getArgument = (
  startDate: Date,
  endDate: Date,
  cohortYear: number,
  key: keyof StudentListTimeSeriesArgumentInput,
): StudentListTimeSeriesArgumentInput => {
  const argument: StudentListTimeSeriesArgumentInput = {
    [key]: {
      dateRange: {
        inclusiveEnd: getDateString(endDate),
        start: getDateString(startDate),
      },
      filters: {},
    },
  } as unknown as StudentListTimeSeriesArgumentInput;

  return argument;
};

function aggregateWeeklyData(
  data: { date: string; current: number }[],
): { date: string; current: number }[] {
  const inputForAggregation: RawDataPoint[] = data.map(({ date, current }) => ({
    date: parse(date, "yyyy-MM-dd", new Date()),
    value: current,
  }));

  const aggregatedData = aggregateData(inputForAggregation, "weekly");

  return aggregatedData.map((item) => ({
    date: getDateString((item as AggregatedDataPoint).periodStart),
    current: item.value,
  }));
}

function aggregateMonthlyData(
  data: { date: string; current: number }[],
): { date: string; current: number }[] {
  const inputForAggregation: RawDataPoint[] = data.map(({ date, current }) => ({
    date: parse(date, "yyyy-MM-dd", new Date()),
    value: current,
  }));

  const aggregatedData = aggregateData(inputForAggregation, "monthly");
  const typedAggregatedData: AggregatedDataPoint[] =
    aggregatedData as AggregatedDataPoint[];

  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const hasCurrentMonth = typedAggregatedData.some(
    (item) =>
      item.periodStart &&
      item.periodStart.getTime() === currentMonthStart.getTime(),
  );

  if (!hasCurrentMonth) {
    const currentMonthData = inputForAggregation.filter(
      (point) =>
        point.date.getFullYear() === now.getFullYear() &&
        point.date.getMonth() === now.getMonth(),
    );

    if (currentMonthData.length > 0) {
      const currentMonthValue = currentMonthData.reduce(
        (sum, point) => sum + point.value,
        0,
      );

      const currentMonthItem: AggregatedDataPoint = {
        date: format(currentMonthStart, "MMM"),
        value: currentMonthValue,
        isPartial: true,
        periodStart: currentMonthStart,
      };

      typedAggregatedData.push(currentMonthItem);
    }
  }

  return typedAggregatedData.map((item) => ({
    date: getDateString(item.periodStart),
    current: item.value,
  }));
}

function chartProps(
  data: { date: string; current: number }[],
  startDate: Date,
  endDate: Date,
  interval: DataInterval,
) {
  const aggregatedData =
    interval === "weekly"
      ? aggregateWeeklyData(data)
      : aggregateMonthlyData(data);

  const getTicks = () => {
    const totalMonths = differenceInMonths(endDate, startDate);
    const isLongRange = totalMonths > 6;

    const ticks: string[] = [];
    let lastLabeledMonth = -1;
    let skipNext = false;

    for (const dataPoint of aggregatedData) {
      const date = parse(dataPoint.date, "yyyy-MM-dd", new Date());
      const currentMonth = date.getMonth();
      const currentYear = date.getFullYear();
      const monthYear = currentYear * 12 + currentMonth;

      if (monthYear !== lastLabeledMonth) {
        if (isLongRange) {
          if (!skipNext) {
            ticks.push(dataPoint.date);
          }
          skipNext = !skipNext;
        } else {
          ticks.push(dataPoint.date);
        }
        lastLabeledMonth = monthYear;
      }
    }

    return ticks;
  };

  return {
    chartData: aggregatedData,
    getTicks,
    tickFormatter: (value: string | number | Date) => {
      const date =
        typeof value === "string"
          ? parse(value, "yyyy-MM-dd", new Date())
          : new Date(value);
      return format(date, "MMM");
    },
  };
}
